import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckCircle2, KeyRound, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'

const nameSchema = z.object({
  name: z.string().min(1, 'Name is required'),
})

const passwordSchema = z.object({
  current_password: z.string().min(1, 'Required'),
  new_password: z.string().min(12, 'Must be at least 12 characters'),
  confirm_password: z.string().min(1, 'Required'),
}).refine(d => d.new_password === d.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
})

const posPinSchema = z.object({
  current_password: z.string().min(1, 'Required'),
  pos_pin: z.string().regex(/^\d{4,6}$/, 'PIN must be 4–6 digits'),
  confirm_pin: z.string().min(1, 'Required'),
}).refine(d => d.pos_pin === d.confirm_pin, {
  message: 'PINs do not match',
  path: ['confirm_pin'],
})

const clearPosPinSchema = z.object({
  current_password: z.string().min(1, 'Required'),
})

type NameForm = z.infer<typeof nameSchema>
type PasswordForm = z.infer<typeof passwordSchema>
type PosPinForm = z.infer<typeof posPinSchema>
type ClearPosPinForm = z.infer<typeof clearPosPinSchema>

const POS_PIN_ELIGIBLE_ROLES = new Set(['super_admin', 'operations'])

export default function Profile() {
  const { user, updateProfile, refreshUser } = useAuth()

  return (
    <div className="p-6 max-w-xl">
      <h1 className="text-lg font-semibold text-white mb-6">My Profile</h1>

      <div className="space-y-5">
        <AccountSection user={user} onNameSaved={updateProfile} />
        <PasswordSection />
        {user && POS_PIN_ELIGIBLE_ROLES.has(user.role) && (
          <PosPinSection user={user} onChanged={refreshUser} />
        )}
      </div>
    </div>
  )
}

function AccountSection({
  user,
  onNameSaved,
}: {
  user: { name: string; email: string; role: string } | null
  onNameSaved: (name: string) => void
}) {
  const [saved, setSaved] = useState(false)
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<NameForm>({
    resolver: zodResolver(nameSchema),
    defaultValues: { name: user?.name ?? '' },
  })

  async function onSubmit(data: NameForm) {
    await api.patch('/auth/me', { name: data.name })
    onNameSaved(data.name)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  return (
    <section className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
      <h2 className="text-sm font-semibold text-white mb-4">Account Details</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Display Name</label>
          <input
            {...register('name')}
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Email</label>
          <input
            value={user?.email ?? ''}
            readOnly
            className="w-full bg-[#1e1e1e] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Role</label>
          <input
            value={user?.role ?? ''}
            readOnly
            className="w-full bg-[#1e1e1e] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed capitalize"
          />
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {isSubmitting ? 'Saving…' : 'Save Name'}
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <CheckCircle2 size={13} /> Saved
            </span>
          )}
        </div>
      </form>
    </section>
  )
}

function PasswordSection() {
  const [saved, setSaved] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  })

  async function onSubmit(data: PasswordForm) {
    setServerError(null)
    try {
      await api.patch('/auth/me', {
        current_password: data.current_password,
        new_password: data.new_password,
      })
      reset()
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      if (code === 'wrong_password') {
        setServerError('Current password is incorrect.')
      } else if (code === 'password_reused') {
        setServerError('This password matches one you\'ve used recently. Choose one you haven\'t used in your last four passwords.')
      } else {
        setServerError('Something went wrong. Please try again.')
      }
    }
  }

  return (
    <section className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
      <h2 className="text-sm font-semibold text-white mb-4">Change Password</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Current Password</label>
          <input
            {...register('current_password')}
            type="password"
            autoComplete="current-password"
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {errors.current_password && <p className="text-xs text-red-400 mt-1">{errors.current_password.message}</p>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">New Password</label>
          <input
            {...register('new_password')}
            type="password"
            autoComplete="new-password"
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {errors.new_password && <p className="text-xs text-red-400 mt-1">{errors.new_password.message}</p>}
          <p className="text-xs text-gray-600 mt-1">Minimum 12 characters</p>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Confirm New Password</label>
          <input
            {...register('confirm_password')}
            type="password"
            autoComplete="new-password"
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {errors.confirm_password && <p className="text-xs text-red-400 mt-1">{errors.confirm_password.message}</p>}
        </div>
        {serverError && <p className="text-xs text-red-400">{serverError}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            {isSubmitting ? 'Updating…' : 'Update Password'}
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <CheckCircle2 size={13} /> Password updated
            </span>
          )}
        </div>
      </form>
    </section>
  )
}

// POS manager PIN — super_admin / operations only. The cashier types this
// at the till when an action exceeds their permissions (large discount,
// void of an older sale). Per-admin PIN (vs a single shared store PIN)
// means every override at the till is attributable to a specific manager
// in the audit log. The PIN never leaves the admin portal except as an
// HMAC-verified override token bound to the requested action.
function PosPinSection({
  user,
  onChanged,
}: {
  user: { name: string; email: string; role: string; pos_pin_set?: boolean }
  onChanged: () => Promise<void>
}) {
  const pinIsSet = user.pos_pin_set === true
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [clearMode, setClearMode] = useState(false)

  const setForm = useForm<PosPinForm>({ resolver: zodResolver(posPinSchema) })
  const clearForm = useForm<ClearPosPinForm>({ resolver: zodResolver(clearPosPinSchema) })

  async function submitSet(data: PosPinForm) {
    setServerError(null)
    try {
      await api.patch('/auth/me', {
        current_password: data.current_password,
        pos_pin: data.pos_pin,
      })
      setForm.reset()
      setSavedMessage(pinIsSet ? 'POS manager PIN updated' : 'POS manager PIN set')
      await onChanged()
      setTimeout(() => setSavedMessage(null), 3000)
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setServerError(
        code === 'wrong_password' ? 'Current password is incorrect.' :
        msg ?? 'Something went wrong. Please try again.'
      )
    }
  }

  async function submitClear(data: ClearPosPinForm) {
    setServerError(null)
    try {
      await api.patch('/auth/me', {
        current_password: data.current_password,
        clear_pos_pin: true,
      })
      clearForm.reset()
      setClearMode(false)
      setSavedMessage('POS manager PIN removed')
      await onChanged()
      setTimeout(() => setSavedMessage(null), 3000)
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      setServerError(code === 'wrong_password' ? 'Current password is incorrect.' : 'Something went wrong. Please try again.')
    }
  }

  return (
    <section className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <KeyRound size={14} className="text-gray-500" />
          <h2 className="text-sm font-semibold text-white">POS Manager PIN</h2>
        </div>
        {pinIsSet ? (
          <span className="px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[11px] font-semibold">
            PIN set
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 text-[11px] font-semibold">
            Not set
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Cashiers at the till type this PIN when they need manager authorisation (large discount or void of an older sale). 4–6 digits. Every override is recorded in the audit log against your account.
      </p>

      {clearMode ? (
        <form onSubmit={clearForm.handleSubmit(submitClear)} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Current Password</label>
            <input
              {...clearForm.register('current_password')}
              type="password"
              autoComplete="current-password"
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
            {clearForm.formState.errors.current_password && (
              <p className="text-xs text-red-400 mt-1">{clearForm.formState.errors.current_password.message}</p>
            )}
          </div>
          {serverError && <p className="text-xs text-red-400">{serverError}</p>}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={clearForm.formState.isSubmitting}
              className="px-3 py-1.5 bg-red-500/20 border border-red-500/50 hover:bg-red-500/30 disabled:opacity-50 text-red-100 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5"
            >
              <Trash2 size={12} /> {clearForm.formState.isSubmitting ? 'Removing…' : 'Confirm remove PIN'}
            </button>
            <button
              type="button"
              onClick={() => { setClearMode(false); setServerError(null); clearForm.reset() }}
              className="px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={setForm.handleSubmit(submitSet)} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Current Password</label>
            <input
              {...setForm.register('current_password')}
              type="password"
              autoComplete="current-password"
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
            {setForm.formState.errors.current_password && (
              <p className="text-xs text-red-400 mt-1">{setForm.formState.errors.current_password.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">{pinIsSet ? 'New PIN' : 'PIN'} (4–6 digits)</label>
            <input
              {...setForm.register('pos_pin')}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] tracking-widest font-mono"
            />
            {setForm.formState.errors.pos_pin && (
              <p className="text-xs text-red-400 mt-1">{setForm.formState.errors.pos_pin.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">Confirm PIN</label>
            <input
              {...setForm.register('confirm_pin')}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] tracking-widest font-mono"
            />
            {setForm.formState.errors.confirm_pin && (
              <p className="text-xs text-red-400 mt-1">{setForm.formState.errors.confirm_pin.message}</p>
            )}
          </div>
          {serverError && <p className="text-xs text-red-400">{serverError}</p>}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={setForm.formState.isSubmitting}
              className="px-4 py-2 bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              {setForm.formState.isSubmitting ? 'Saving…' : pinIsSet ? 'Update PIN' : 'Set PIN'}
            </button>
            {pinIsSet && (
              <button
                type="button"
                onClick={() => { setClearMode(true); setServerError(null) }}
                className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1 transition-colors"
              >
                <Trash2 size={12} /> Remove PIN
              </button>
            )}
            {savedMessage && (
              <span className="flex items-center gap-1.5 text-xs text-green-400">
                <CheckCircle2 size={13} /> {savedMessage}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  )
}
